import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-47ef139e"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "form-new-discount-code__fields" }
const _hoisted_3 = { class: "form-new-discount-code__sale-articles" }
const _hoisted_4 = { class: "form-new-discount-code__actions" }

import { ref, computed, onMounted, watch } from 'vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseTag from '@/components/BaseTag.vue'
import DatePicker from '@/components/DatePicker.vue'
import { FORMATS } from '@/enums/components'
import { useRoute, useRouter } from 'vue-router'
import { ROUTE_PATHS } from '@/router/paths'
import { useAuthStore } from '@/store/auth'
import { storeToRefs } from 'pinia'
import {
  createDiscountCode,
  useDiscountDetailQuery,
  useUpdateDiscountCodeMutation,
} from '@/queries/discount'
import { useServiceNamesQuery } from '@/queries/services'
import { useI18n } from 'vue-i18n'
import { isBefore, startOfDay } from 'date-fns'

interface SaleArticle {
  id?: number
  name: string
}

interface DiscountResponse {
  uuid: string
  name: string
  discount_percent: number
  valid_from: string
  valid_until: string | null
  sale_articles: SaleArticle[]
  combinable: boolean
  number_of_times: number
  code_used: number
  status: string
}

interface FormData {
  code: string
  start_time: string
  end_time: string | null
  sale_articles: { value: string; label: string }[] | null
  number_of_times: number
  times_used: number
  discount_percent: number
}

interface Option {
  value: string
  label: string
  key?: string | number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FormNewDiscountCode',
  setup(__props) {

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { selectedCompanyId } = storeToRefs(authStore)

const discountUuid = computed(() => (route.params.uuid as string) || null)
const isEditMode = computed(() => !!discountUuid.value)

const { data: serviceNames, isLoading: servicesLoading } =
  useServiceNamesQuery(selectedCompanyId)
const saleArticleOptions = computed<Option[]>(() => {
  if (!serviceNames.value) return []
  return serviceNames.value
    .filter((service) => service.id)
    .map((service) => ({
      value: service.id?.toString() ?? '',
      label: service.name ?? '',
      key: service.id?.toString() ?? '',
    }))
})

const updateDiscountMutation = useUpdateDiscountCodeMutation()

const shouldFetchDiscountDetail = computed(() => {
  return !!(isEditMode.value && discountUuid.value && selectedCompanyId.value)
})

const {
  data: discountDetail,
  isLoading: detailLoading,
  isError: detailError,
  error: detailErrorMessage,
} = useDiscountDetailQuery(
  selectedCompanyId,
  discountUuid,
  shouldFetchDiscountDetail,
)

const currentTimestamp = computed(() => new Date())

const formData = ref<FormData>({
  code: '',
  start_time: currentTimestamp.value.toISOString(),
  end_time: null,
  sale_articles: [],
  number_of_times: 0,
  times_used: 0,
  discount_percent: 0,
})

const errors = ref({
  code: '',
  start_time: '',
  end_time: '',
})

const initializeFormData = () => {
  if (!isEditMode.value || !discountDetail.value) return

  const discount = discountDetail.value as DiscountResponse

  formData.value = {
    code: discount.name,
    start_time: discount.valid_from,
    end_time: discount.valid_until,
    sale_articles:
      discount.sale_articles?.length > 0
        ? (discount.sale_articles
            .map((article) =>
              saleArticleOptions.value.find(
                (option) => option.value === article.id?.toString(),
              ),
            )
            .filter(Boolean) as { value: string; label: string }[])
        : [],
    number_of_times: discount.number_of_times,
    times_used: discount.code_used,
    discount_percent: discount.discount_percent,
  }
}

watch(
  [discountDetail, saleArticleOptions],
  () => {
    if (discountDetail.value && saleArticleOptions.value.length > 0) {
      initializeFormData()
    }
  },
  { immediate: true },
)
watch(
  isEditMode,
  (newValue) => {
    if (newValue && discountDetail.value) {
      initializeFormData()
    }
  },
  { immediate: true },
)

const isFormValid = computed(() => {
  const hasRequiredFields =
    formData.value.code.trim() !== '' && formData.value.start_time !== null

  const hasNoErrors =
    errors.value.code === '' &&
    errors.value.start_time === '' &&
    errors.value.end_time === ''

  const hasValidDates =
    formData.value.start_time &&
    (!formData.value.end_time ||
      new Date(formData.value.end_time) > new Date(formData.value.start_time))

  return hasRequiredFields && hasNoErrors && hasValidDates
})

function handleRemove(key: string) {
  formData.value.sale_articles =
    formData.value.sale_articles?.filter((item) => item.key !== key) || []
}

const formatDate = (date: Date | string | null): string | null => {
  if (!date) return null
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

const validateCode = () => {
  if (!formData.value.code.trim()) {
    errors.value.code = 'Code is required'
  } else {
    errors.value.code = ''
  }
}

const validateStartTime = () => {
  if (!formData.value.start_time) {
    errors.value.start_time = 'Start time is required'
  } else if (
    isBefore(
      startOfDay(new Date(formData.value.start_time)),
      startOfDay(new Date()),
    )
  ) {
    errors.value.start_time = 'Start time cannot be in the past'
  } else {
    errors.value.start_time = ''
  }
}

const validateEndTime = () => {
  if (formData.value.end_time && formData.value.start_time) {
    if (
      new Date(formData.value.end_time) < new Date(formData.value.start_time)
    ) {
      errors.value.end_time = 'End time must be after start time'
    } else {
      errors.value.end_time = ''
    }
  }
}

const handleUpdate = (selectedOptions: Option[]) => {
  formData.value.sale_articles = selectedOptions || []
}

const handleSubmit = async () => {
  if (!isFormValid.value) {
    return
  }

  if (selectedCompanyId.value === null) {
    console.error('No company selected.')
    return
  }

  const payload = {
    name: formData.value.code,
    discount_percent: formData.value.discount_percent,
    valid_from: formatDate(formData.value.start_time),
    valid_until: formatDate(formData.value.end_time),
    service_ids: formData.value.sale_articles
      ? formData.value.sale_articles.map((article) =>
          parseInt(article.value, 10),
        )
      : [],
    combinable: false,
    number_of_times: formData.value.number_of_times,
  }

  try {
    if (isEditMode.value && discountUuid.value) {
      await updateDiscountMutation.mutateAsync({
        companyId: selectedCompanyId.value,
        discountUuid: discountUuid.value,
        payload,
      })
    } else {
      await createDiscountCode(selectedCompanyId.value, payload)
    }

    router.push({ name: ROUTE_PATHS.DISCOUNT_CODES })
  } catch (error) {
    console.error('Error saving discount code:', error)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form-new-discount-code",
    onSubmit: _withModifiers(handleSubmit, ["prevent"])
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(BaseInput, {
        modelValue: formData.value.code,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.code) = $event)),
        label: _unref(t)('discountCodes.code'),
        name: "code",
        required: "",
        error: errors.value.code,
        placeholder: _unref(t)('discountCodes.enterPromotionalCode'),
        onInput: validateCode
      }, null, 8, ["modelValue", "label", "error", "placeholder"]),
      _createVNode(DatePicker, {
        modelValue: formData.value.start_time,
        "onUpdate:modelValue": [
          _cache[1] || (_cache[1] = ($event: any) => ((formData.value.start_time) = $event)),
          validateStartTime
        ],
        label: _unref(t)('discountCodes.startTime'),
        name: "start_time",
        required: "",
        error: errors.value.start_time,
        "default-value": _unref(currentTimestamp),
        "min-date": _unref(currentTimestamp)
      }, null, 8, ["modelValue", "label", "error", "default-value", "min-date"]),
      _createVNode(DatePicker, {
        modelValue: formData.value.end_time,
        "onUpdate:modelValue": [
          _cache[2] || (_cache[2] = ($event: any) => ((formData.value.end_time) = $event)),
          validateEndTime
        ],
        label: _unref(t)('discountCodes.endTime'),
        name: "end_time",
        error: errors.value.end_time,
        "min-date": formData.value.start_time || _unref(currentTimestamp)
      }, null, 8, ["modelValue", "label", "error", "min-date"]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(BaseSelect, {
          modelValue: formData.value.sale_articles,
          "onUpdate:modelValue": [
            _cache[3] || (_cache[3] = ($event: any) => ((formData.value.sale_articles) = $event)),
            handleUpdate
          ],
          label: _unref(t)('discountCodes.saleArticles'),
          name: "sale_articles",
          options: _unref(saleArticleOptions),
          "track-by": 'value',
          "option-label": 'label',
          "return-object": "",
          searchable: true,
          "clear-on-select": true,
          "preserve-search": true,
          multiple: "",
          onRemove: _cache[4] || (_cache[4] = (obj) => handleRemove(obj.key))
        }, null, 8, ["modelValue", "label", "options"])
      ]),
      _createVNode(BaseInput, {
        modelValue: formData.value.number_of_times,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.number_of_times) = $event)),
        label: _unref(t)('discountCodes.numberOfTimes'),
        name: "number_of_times",
        type: "number",
        min: "1"
      }, null, 8, ["modelValue", "label"]),
      _createVNode(BaseInput, {
        modelValue: formData.value.discount_percent,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.value.discount_percent) = $event)),
        label: _unref(t)('discountCodes.discountPercentage'),
        name: "discount_percent",
        type: "number",
        min: "1",
        max: "100"
      }, null, 8, ["modelValue", "label"]),
      _createVNode(BaseInput, {
        modelValue: formData.value.times_used,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.value.times_used) = $event)),
        label: _unref(t)('discountCodes.timesUsed'),
        name: "times_used",
        type: "number",
        min: "0",
        disabled: ""
      }, null, 8, ["modelValue", "label"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(BaseButton, {
        type: "submit",
        format: _unref(FORMATS).PRIMARY,
        label: _unref(t)('save'),
        disabled: !_unref(isFormValid)
      }, null, 8, ["format", "label", "disabled"])
    ])
  ], 40, _hoisted_1))
}
}

})